/**
 * Safari Builder Context
 * Manages global state for the Custom Safari Builder
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Tour } from '../types';

interface SafariBuilderContextType {
  isBuilderOpen: boolean;
  openBuilder: (tours?: Tour[]) => void;
  closeBuilder: () => void;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  initialTours: Tour[];
  addTourToBuilder: (tour: Tour) => void; // New: Add tour when builder is already open
  registerAddTourCallback?: (callback: (tour: Tour) => void) => (() => void); // Internal: Register callback from builder
}

const SafariBuilderContext = createContext<SafariBuilderContextType | undefined>(undefined);

export const useSafariBuilder = () => {
  const context = useContext(SafariBuilderContext);
  if (context === undefined) {
    throw new Error('useSafariBuilder must be used within a SafariBuilderProvider');
  }
  return context;
};

interface SafariBuilderProviderProps {
  children: ReactNode;
}

export const SafariBuilderProvider: React.FC<SafariBuilderProviderProps> = ({ children }) => {
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [initialTours, setInitialTours] = useState<Tour[]>([]);
  const addTourCallbackRef = React.useRef<((tour: Tour) => void) | null>(null);

  const openBuilder = (tours?: Tour[]) => {
    if (tours) {
      setInitialTours(tours);
    } else {
      setInitialTours([]);
    }
    setIsBuilderOpen(true);
    setIsCartOpen(false);
  };

  const closeBuilder = () => {
    setIsBuilderOpen(false);
    setInitialTours([]);
    addTourCallbackRef.current = null; // Clear callback when closing
  };

  const openCart = () => {
    setIsCartOpen(true);
    setIsBuilderOpen(false);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const addTourToBuilder = (tour: Tour) => {
    // If builder is open, call the callback to add the tour
    if (addTourCallbackRef.current) {
      addTourCallbackRef.current(tour);
    } else {
      // If builder is not open, open it with this tour
      openBuilder([tour]);
    }
  };

  // Expose method to register the addTour callback from CustomSafariBuilder
  const registerAddTourCallback = React.useCallback((callback: (tour: Tour) => void) => {
    addTourCallbackRef.current = callback;
    return () => {
      addTourCallbackRef.current = null;
    };
  }, []);

  return (
    <SafariBuilderContext.Provider
      value={{
        isBuilderOpen,
        openBuilder,
        closeBuilder,
        isCartOpen,
        openCart,
        closeCart,
        initialTours,
        addTourToBuilder,
        registerAddTourCallback,
      }}
    >
      {children}
    </SafariBuilderContext.Provider>
  );
};

